import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { requestNativePermission } from "../../utils/nativePermissions";
import {
  advanceFromKey,
  isAddonFlowActive,
  backFromKey,
  addonDialogReturnPath,
} from "../../lib/addonIntroFlow";
import useStepBackGuard from "../../hooks/useStepBackGuard";
import { fetchSetupScore } from "../../lib/setupScore";
import styles from "./ScreenCallsIntro.module.css";
import AnimatedButton from "../AnimatedButton/AnimatedButton";

/*
 * ScreenCallsIntro — "Let {Agent} screen your calls" (Contact Access).
 *
 * Reached from the SophiaReadyDialog "Turn on spam filtering" and
 * "Get Add-Ons" cards on /go-live-setup.
 *
 * CTAs:
 *   - Allow Contact Access → ask the native shell for the CONTACTS
 *     permission (no-op "unavailable" in a plain browser), then route
 *     to /contact-sync which runs the actual sync flow.
 *   - Skip — I'll set this up later → /calendar-access (the calendar
 *     intro nudge).
 */

const readAgentCtx = () => {
  try {
    const raw = sessionStorage.getItem("agentDetails");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const Check = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M3.5 8L6.5 11L12.5 5"
      stroke="#fff"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LockIcon = () => (
  <svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="22.5" cy="22.5" r="22" fill="#FDE7D7" stroke="white" />
    <path d="M26.5567 20.2785V16.4646C26.5567 14.0117 24.4519 12.0156 21.8651 12.0156C19.2783 12.0156 17.1734 14.0116 17.1734 16.4646V20.2785C15.6954 20.2785 14.4922 21.4195 14.4922 22.8211V27.9052C14.4922 29.3068 15.6953 30.4478 17.1734 30.4478H26.5567C28.0347 30.4478 29.238 29.3069 29.238 27.9052V22.8211C29.238 21.4195 28.0348 20.2785 26.5567 20.2785ZM22.5348 26.6339C22.5348 26.9832 22.2333 27.2691 21.865 27.2691C21.4966 27.2691 21.1951 26.9832 21.1951 26.6339V24.0913C21.1951 23.742 21.4966 23.4561 21.865 23.4561C22.2333 23.4561 22.5348 23.742 22.5348 24.0913V26.6339ZM25.2161 20.2785H18.5141V16.4646C18.5141 14.7137 20.0187 13.2869 21.8651 13.2869C23.7114 13.2869 25.2161 14.7137 25.2161 16.4646V20.2785Z" fill="#FF6801" />
  </svg>


);

export default function ScreenCallsIntro() {
  const navigate = useNavigate();
  const location = useLocation();
  const [requesting, setRequesting] = useState(false);

  /*
   * ── Already-done self-skip ──────────────────────────────────────
   *
   * THIS is the sequence's "contact" screen — ADDON_ROUTE_BY_KEY maps
   * contact → /screen-your-calls. (/contact-sync is downstream, reached only
   * after "Allow Contact Access", so a guard there never runs for a user who
   * never taps Allow. That was the bug: the skip was implemented one screen
   * too late.)
   *
   * Same rule as the calendar screen: if the feature is already handled,
   * advance the sequence instead of asking again.
   *
   * "Handled" = the setup-score `contactsSynced` step is ticked. Not
   * `isSyncContactSkip` (a plain Skip tap writes that too, which would make one
   * Skip permanent), not `hasToken`, and not contact ROWS — see the note at the
   * check itself; a calendar connect can produce both of those.
   *
   * Guarded on isAddonFlowActive(): outside a sequence there is nothing to
   * advance to, and advanceFromKey() would clear the flow and bounce the user
   * to the dashboard — a direct visit must still render.
   */
  useEffect(() => {
    if (!isAddonFlowActive()) return;
    let cancelled = false;
    (async () => {
      try {
        const tempCtx = readAgentCtx();
        const agentId = tempCtx?.agent?.agent_id || tempCtx?.agentId || null;
        const scoreRes = await fetchSetupScore(agentId).catch(() => null);
        if (cancelled) return;
        const scoreSynced = scoreRes?.steps?.contactsSynced?.done === true;

        /*
         * "Handled" = the setup-score `contactsSynced` step is ticked. That mark
         * is written ONLY by a deliberate user action — a Google contacts sync,
         * a CSV import, the phone picker, or a manual add (ContactsPage), and by
         * GoogleAuthStep only when `contacts.readonly` is actually present in the
         * granted scope set. No other feature can produce it.
         *
         * Deliberately NOT `hasToken`. getGoogleSyncStatus's hasToken has a
         * back-compat branch (Contactcontroller.js:721):
         *   scopeStr === "" || scopeStr.includes("contacts.readonly")
         * and the CALENDAR OAuth writes `scope: tokens.scope || null`
         * (googleAuth.js:688) — a null scope reads back as "", so merely
         * connecting the calendar flipped hasToken true.
         *
         * And deliberately NOT contact ROWS either. An earlier version of this
         * check used them, on the reasoning that rows were "the one signal a
         * calendar connect cannot fabricate". That reasoning was wrong. The
         * calendar-only OAuth callback runs a BONUS contacts import
         * (googleAuth.js:698-706) gated on
         * `tokens.scope.includes("contacts.readonly")`, and because
         * connectCalendarOnly sets `include_granted_scopes: true`
         * (googleAuth.js:559) Google echoes back any contacts scope the account
         * granted at ANY point in the past. For such a user — the norm on the
         * native app, where Google sign-in requests contacts (googleAuth.js:179)
         * — connecting the calendar silently bulk-inserts their whole Google
         * address book. Rows then existed for someone who had never touched
         * contacts: this screen skipped itself, the calendar screen skipped
         * itself too (already connected), and the whole add-on sequence
         * collapsed straight to its finish route with nothing shown.
         *
         * Accepted cost: someone who synced contacts BEFORE the setup score
         * existed (rows, but no mark) is asked once more; acting on the screen
         * credits the step and they are never asked again. Asking again is the
         * safe failure; silently skipping a step they never completed is not.
         */
        const contactsHandled = scoreSynced;
        if (contactsHandled) {
          navigate(advanceFromKey("contact"), { replace: true });
        }
      } catch (_e) {
        // Can't tell → render the screen. Asking again is the safe failure.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ctx = readAgentCtx();
  const agentName = ctx?.agent?.agentName || "Sophia";

  // Forward whatever query string we arrived with (agentId / userId)
  // to the next screen so downstream routes keep their context.
  const search = location.search || "";

  const handleAllow = async () => {
    if (requesting) return;
    setRequesting(true);
    try {
      // Native shell renders the soft-ask + OS prompt and replies with
      // a status. On plain web this resolves "unavailable" immediately
      // and /contact-sync's SyncDialog takes over. The add-on queue
      // stays in sessionStorage — ContactSyncStep's "Complete Setup"
      // advances to the next screen (calendar) when it's still queued.
      await requestNativePermission("CONTACTS", {
        app: "Rexpt",
        agent: agentName,
      });
    } catch (err) {
      console.warn("[ScreenCallsIntro] contact permission request failed:", err);
    } finally {
      navigate(`/contact-sync${search}`);
    }
  };

  // Skip advances the sequence FROM the contact screen: next screen
  // (calendar) or, if contact is last, the dashboard. Non-mutating —
  // browser Back can re-land here and advance again correctly.
  const handleSkip = () => {
    navigate(advanceFromKey("contact"));
  };

  // Device/browser back walks the sequence backward (→ previous screen),
  // or, when contact is the FIRST screen, returns to the "Sophia is
  // live" add-ons popup on /go-live-setup. Never escapes to Stripe.
  useStepBackGuard({
    canGoBack: () => backFromKey("contact") !== null,
    onBack: () => navigate(backFromKey("contact")),
    onExit: () => navigate(addonDialogReturnPath()),
  });

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <h1 className={styles.title}>
          <span className={styles.titleLight}>Let {agentName}</span>
          <span className={styles.titleBold}>screen your calls</span>
        </h1>

        <p className={styles.subtitle}>
          She greets the people you know by name — and{" "}
          <span className={styles.subtitleStrong}>screens the ones</span> not on
          your contact book.
        </p>

        <div className={styles.howCard}>
          <h2 className={styles.howTitle}>How it works:</h2>
          <ul className={styles.howList}>
            <li className={styles.howItem}>
              <span className={styles.checkBadge}>
                <Check />
              </span>
              Greets your contacts by name
            </li>
            <li className={styles.howItem}>
              <span className={styles.checkBadge}>
                <Check />
              </span>
              Screens &amp; qualifies unknown callers
            </li>
            <li className={styles.howItem}>
              <span className={styles.checkBadge}>
                <Check />
              </span>
              Sends VIPs straight to you, blocks the spam
            </li>
          </ul>
        </div>

        <div className={styles.privacyChip}>
          <span className={styles.privacyIcon}>
            <LockIcon />
          </span>
          <span className={styles.privacyText}>
            <b className={styles.privacyTextBold}>
              {agentName} only reads names and numbers
            </b>{" "}
            to recognize callers. Nothing is stored, shared, or used for
            marketing.
          </span>
        </div>
      </div>

      <div className={styles.footer}>
        <AnimatedButton
          label={
            requesting ? (
              "Requesting…"
            ) : (
              <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                Allow Contact Access
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path d="M7.33944 13.093L5.94953 11.7166L10.1125 7.55362H-0.00146484V5.54297H10.1125L5.94953 1.38672L7.33944 0.00355083L13.8842 6.5483L7.33944 13.093Z" fill="white" />
                </svg>
              </span>
            )
          }
          onClick={handleAllow}
          disabled={requesting}
          positionType="relative"
          width="100%"
          bottom="0px"
        />
        <button type="button" className={styles.skipLink} onClick={handleSkip}>
          Skip — I'll set this up later
        </button>
      </div>
    </div>
  );
}
